import { useMemo, useState, type ReactNode } from 'react'

import { PageBody, PageHeader } from '@/components/AppShell'
import { ErrorState, TableSkeleton } from '@/components/states'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { centsLabel, rechargeStateNote } from '@/lib/billing'
import { formatAbsolute, formatBytes, formatMicros } from '@/lib/format'

import { useBillingHealth, useHealthLive, useHealthSeries, type HealthLive, type RefundIssue, type StuckRecharge } from '../api'
import { compact } from '../chart'
import { LineChart } from '../LineChart'
import { RANGES, windowFor, type RangeKey } from '../ranges'
import { useStaff } from '../hooks'
import { RefundStateBadge, ResolveRefundDialog, SettleDialog } from '../refunds'
import { ActionNotice, StaffGate, StaffLink } from '../ui'
import { SELECT_CLASS } from '../shared'

function Tile({ label, value, hint, testId }: { label: string; value: string; hint?: string; testId: string }) {
  return (
    <div className="min-w-0 rounded-lg border border-border bg-card px-4 py-3" data-testid={testId}>
      <p className="truncate text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tabular-nums tracking-tight">{value}</p>
      {hint === undefined ? null : <p className="truncate text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

function sum(record: Record<string, number>): number {
  return Object.values(record).reduce((a, b) => a + b, 0)
}

function LiveTiles({ live }: { live: HealthLive }) {
  const p = live.platform
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
      <Tile testId="tile-transports" label="Active transports" value={compact(p.transports_mcp + p.transports_agent)} hint={`${p.transports_mcp} MCP · ${p.transports_agent} agent`} />
      <Tile testId="tile-identities" label="Identities online" value={compact(p.identities_online)} hint={`${p.identities_offline} offline`} />
      <Tile testId="tile-waits" label="Waits in flight" value={compact(p.waits_in_flight)} hint={`${live.replica.waits_in_flight} on this replica`} />
      <Tile testId="tile-sent" label="Messages sent / min" value={compact(p.messages_sent_per_minute)} hint={`${compact(p.messages_delivered_per_minute)} delivered / min`} />
      <Tile testId="tile-queue" label="Queue depth" value={compact(p.queue_depth)} hint="pending messages, all zones" />
      <Tile testId="tile-errors" label="Errors / min" value={compact(sum(p.errors_per_minute))} hint={`${Object.keys(p.errors_per_minute).length} codes`} />
      <Tile testId="tile-frames" label="Frames / min" value={compact(sum(p.frames_per_minute))} />
      <Tile testId="tile-uploads" label="Uploads / min" value={formatBytes(p.uploads_bytes_per_minute)} />
    </div>
  )
}

function Breakdowns({ live }: { live: HealthLive }) {
  const p = live.platform
  const errors = Object.entries(p.errors_per_minute).sort((a, b) => b[1] - a[1])
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>Deepest queues</CardTitle>
          <CardDescription>Top zones by pending messages.</CardDescription>
        </CardHeader>
        <CardContent>
          {p.queue_top_zones.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing is queued.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {p.queue_top_zones.map((z) => (
                <li key={z.zone_id} className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate">
                    <StaffLink to={`/app/staff/zones/${z.zone_id}`}>{z.zone_name}</StaffLink>
                  </span>
                  <span className="tabular-nums">{z.depth}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>Errors by code</CardTitle>
          <CardDescription>Last minute.</CardDescription>
        </CardHeader>
        <CardContent>
          {errors.length === 0 ? (
            <p className="text-sm text-muted-foreground">No errors in the last minute.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {errors.map(([code, n]) => (
                <li key={code} className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate font-mono text-xs">{code}</span>
                  <span className="tabular-nums">{n}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <Card className="min-w-0">
        <CardHeader>
          <CardTitle>Client mix</CardTitle>
          <CardDescription>Client name and protocol version.</CardDescription>
        </CardHeader>
        <CardContent>
          {p.client_mix.length === 0 ? (
            <p className="text-sm text-muted-foreground">No sessions.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {p.client_mix.map((c) => (
                <li key={c.client} className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate font-mono text-xs">{c.client}</span>
                  <span className="tabular-nums">{c.sessions}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/** stripe.md §10: every Stripe-side failure is a row here. Read-only, every staff tier. */
function BillingHealthSection() {
  const billing = useBillingHealth()
  const staff = useStaff()
  const canWrite = staff.can('billing_plan_quota')
  const [settling, setSettling] = useState<StuckRecharge | null>(null)
  const [resolving, setResolving] = useState<RefundIssue | null>(null)
  const [notice, setNotice] = useState<{ title: string; notified: boolean | undefined; detail?: ReactNode } | null>(null)
  return (
    <Card data-testid="billing-health">
      <CardHeader>
        <CardTitle>Billing</CardTitle>
        <CardDescription>
          Seat syncs that did not reach Stripe, failed auto-recharges of the last 7 days, and webhook
          events whose processing failed, plus recharges stuck for settling and refunds that did not reach
          the card.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-3 empty:hidden">
          <ActionNotice notice={notice} onDismiss={() => { setNotice(null) }} />
        </div>
        {billing.isError ? (
          <ErrorState error={billing.error} title="Could not load billing health" onRetry={() => void billing.refetch()} />
        ) : billing.isPending ? (
          <TableSkeleton rows={2} columns={2} />
        ) : (
          <>
            <div className="min-w-0" data-testid="billing-seat-failures">
              <p className="mb-2 text-sm font-medium">Seat-sync failures ({billing.data.seat_sync_failures.length})</p>
              {billing.data.seat_sync_failures.length === 0 ? (
                <p className="text-sm text-muted-foreground">None.</p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {billing.data.seat_sync_failures.map((f) => (
                    <li key={f.account_id} className="min-w-0">
                      <StaffLink to={`/app/staff/accounts/${f.account_id}`}>{f.account_id.slice(0, 8)}</StaffLink>{' '}
                      wants {f.target_quantity} extra, {f.failures} failures
                      {f.failure_code === null ? '' : ` (${f.failure_code})`}
                      {f.next_attempt_at === null ? '' : `, next try ${formatAbsolute(f.next_attempt_at)}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="min-w-0" data-testid="billing-recharge-failures">
              <p className="mb-2 text-sm font-medium">Recharge failures ({billing.data.recharge_failures.length})</p>
              {billing.data.recharge_failures.length === 0 ? (
                <p className="text-sm text-muted-foreground">None.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {billing.data.recharge_failures.map((f) => (
                    <li key={f.recharge_id} className="min-w-0">
                      <StaffLink to={`/app/staff/accounts/${f.account_id}`}>{f.account_id.slice(0, 8)}</StaffLink>{' '}
                      <span className="font-mono text-xs">{f.state}</span> {formatMicros(f.amount_micros)}
                      {f.failure_code === null ? '' : ` (${f.failure_code})`}
                      {f.payment_intent_id === null ? null : <span className="block font-mono text-xs text-muted-foreground">{f.payment_intent_id}</span>}
                      <span className="block text-xs text-muted-foreground">{rechargeStateNote(f.state)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="min-w-0" data-testid="billing-stuck-recharges">
              <p className="mb-2 text-sm font-medium">Stuck recharges ({billing.data.stuck_recharges.length})</p>
              {billing.data.stuck_recharges.length === 0 ? (
                <p className="text-sm text-muted-foreground">None.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {billing.data.stuck_recharges.map((r) => (
                    <li key={r.recharge_id} className="min-w-0" data-testid="stuck-recharge">
                      <StaffLink to={`/app/staff/accounts/${r.account_id}`}>{r.account_id.slice(0, 8)}</StaffLink>{' '}
                      {formatMicros(r.amount_micros)} <span className="font-mono text-xs">{r.charge_method ?? ''}</span>
                      <span className="block font-mono text-xs break-all text-muted-foreground">{r.invoice_id ?? r.payment_intent_id ?? r.idempotency_key ?? ''}</span>
                      <span className="block text-xs text-muted-foreground">since {formatAbsolute(r.created_at)}</span>
                      {canWrite ? (
                        r.settleable ? (
                          <Button size="sm" variant="outline" className="mt-1" onClick={() => { setSettling(r) }}>Settle</Button>
                        ) : (
                          <span className="block text-xs text-muted-foreground">Can be settled once it has been stuck for 23 hours.</span>
                        )
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="min-w-0" data-testid="billing-refund-issues">
              <p className="mb-2 text-sm font-medium">
                Refund issues ({billing.data.refund_issues.length})
                {billing.data.pending_refund_requests > 0 ? (
                  <> · <StaffLink to="/app/staff/refunds">{billing.data.pending_refund_requests} request{billing.data.pending_refund_requests === 1 ? '' : 's'} waiting</StaffLink></>
                ) : null}
              </p>
              {billing.data.refund_issues.length === 0 ? (
                <p className="text-sm text-muted-foreground">None.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {billing.data.refund_issues.map((i) => (
                    <li key={i.refund_id} className="min-w-0" data-testid="refund-issue">
                      <StaffLink to={`/app/staff/accounts/${i.account_id}`}>{i.account_id.slice(0, 8)}</StaffLink>{' '}
                      <RefundStateBadge state={i.state} /> {centsLabel(i.total_cents)}
                      {i.failure_code === null ? '' : ` (${i.failure_code})`}
                      <span className="block font-mono text-xs break-all text-muted-foreground">{i.credit_note_id ?? i.invoice_id ?? ''}</span>
                      {canWrite && i.state === 'PENDING_MANUAL' ? (
                        <Button size="sm" variant="outline" className="mt-1" onClick={() => { setResolving(i) }}>Resolve</Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="min-w-0" data-testid="billing-failed-events">
              <p className="mb-2 text-sm font-medium">Failed webhook events ({billing.data.failed_events.length})</p>
              {billing.data.failed_events.length === 0 ? (
                <p className="text-sm text-muted-foreground">None.</p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {billing.data.failed_events.map((e) => (
                    <li key={e.event_id} className="min-w-0">
                      <span className="font-mono text-xs break-all">{e.type}</span>{' '}
                      <span className="text-xs text-muted-foreground">
                        {e.event_id} · {e.attempts} attempts · {formatAbsolute(e.received_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </CardContent>
      {settling === null ? null : (
        <SettleDialog
          recharge={settling}
          onClose={() => { setSettling(null) }}
          onDone={(outcome, notified) => { setNotice({ title: outcome === 'charged' ? 'Recharge settled as charged' : 'Recharge settled as not charged', notified }) }}
        />
      )}
      {resolving === null ? null : (
        <ResolveRefundDialog
          refund={{ id: resolving.refund_id, total_cents: resolving.total_cents, failure_code: resolving.failure_code }}
          onClose={() => { setResolving(null) }}
          onDone={(notified) => { setNotice({ title: 'Refund marked as paid out', notified }) }}
        />
      )}
    </Card>
  )
}

interface ChartDef {
  metric: string
  title: string
  format?: (v: number) => string
}

const CHARTS: readonly ChartDef[] = [
  { metric: 'messages.sent', title: 'Messages sent' },
  { metric: 'messages.delivered', title: 'Messages delivered' },
  { metric: 'queue.depth', title: 'Queue depth' },
  { metric: 'identities.online', title: 'Identities online' },
  { metric: 'waits.in_flight', title: 'Waits in flight' },
  { metric: 'uploads.bytes', title: 'Upload bytes', format: (v) => formatBytes(v) },
]

function SeriesChart({ def, range, win }: { def: ChartDef; range: RangeKey; win: ReturnType<typeof windowFor> }) {
  const series = useHealthSeries(def.metric, win.step, win.from, win.to)
  if (series.isError) return <ErrorState error={series.error} title={`Could not load ${def.title}`} />
  if (series.isPending) return <TableSkeleton rows={2} columns={2} />
  return (
    <LineChart
      key={range}
      title={def.title}
      points={series.data.points}
      from={win.from}
      to={win.to}
      step={win.step}
      {...(def.format === undefined ? {} : { format: def.format })}
    />
  )
}

export function StaffDashboardPage() {
  const live = useHealthLive()
  const [range, setRange] = useState<RangeKey>('1h')
  // Re-read the clock only when the range changes; the window is rounded to the minute inside.
  const win = useMemo(() => windowFor(range, Date.now()), [range])

  return (
    <StaffGate>
      <PageHeader
        eyebrow="Staff"
        title="Platform health"
        description="Live numbers refresh every 15 seconds. Series come from per-minute buckets kept 7 days and hourly buckets kept 90."
      />
      <PageBody>
        {live.isError ? (
          <ErrorState error={live.error} title="Could not load live health" onRetry={() => void live.refetch()} />
        ) : live.isPending ? (
          <TableSkeleton rows={4} columns={4} />
        ) : (
          <>
            <LiveTiles live={live.data} />
            <Breakdowns live={live.data} />
          </>
        )}

        <BillingHealthSection />

        <Card>
          <CardHeader className="sm:flex-row sm:items-end sm:justify-between">
            <div>
              <CardTitle>History</CardTitle>
              <CardDescription>Counters are totals per bucket; gauges are levels.</CardDescription>
            </div>
            <div className="w-full space-y-1.5 sm:w-52">
              <Label htmlFor="range">Range</Label>
              <select
                id="range"
                className={SELECT_CLASS}
                value={range}
                onChange={(e) => {
                  setRange(e.target.value as RangeKey)
                }}
              >
                {RANGES.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </CardHeader>
          <CardContent className="grid gap-6 lg:grid-cols-2">
            {CHARTS.map((def) => (
              <SeriesChart key={def.metric} def={def} range={range} win={win} />
            ))}
          </CardContent>
        </Card>
      </PageBody>
    </StaffGate>
  )
}
